if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        // Set up event listeners with delegation
        this.setupEventListeners();

        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      setupEventListeners() {
        if (!this.elements.viewer || !this.elements.thumbnails) return;
        
        // Use debounce for slideChanged event
        this._boundOnSlideChanged = debounce(this.onSlideChanged.bind(this), 500);
        this.elements.viewer.addEventListener('slideChanged', this._boundOnSlideChanged);
        
        // Use event delegation for thumbnail clicks
        this.elements.thumbnails.addEventListener('click', this.handleThumbnailClick.bind(this));

        // Gallery arrow navigation (desktop: class-based; mobile: SliderComponent handles scroll)
        const prevBtn = this.elements.viewer.querySelector('.product-gallery__slider-button.slider-button--prev');
        const nextBtn = this.elements.viewer.querySelector('.product-gallery__slider-button.slider-button--next');
        if (prevBtn) prevBtn.addEventListener('click', () => this.navigateGallery('prev'));
        if (nextBtn) nextBtn.addEventListener('click', () => this.navigateGallery('next'));
      }

      navigateGallery(direction) {
        // On mobile the SliderComponent scroll handles navigation
        if (!this.mql.matches) return;

        // Use [id^="Slide-"] to target only the <li> slide items, not inner
        // elements (e.g. button.product__media-toggle) that also carry data-media-id
        const slideItems = Array.from(this.elements.viewer.querySelectorAll('[id^="Slide-"]'));
        if (slideItems.length < 2) return;

        const activeItem = slideItems.find((el) => el.classList.contains('is-active'));
        if (!activeItem) return;

        const currentIndex = slideItems.indexOf(activeItem);
        const newIndex =
          direction === 'next'
            ? (currentIndex + 1) % slideItems.length
            : (currentIndex - 1 + slideItems.length) % slideItems.length;

        // dataset.mediaId on the <li> is "sectionId-mediaId", which is what setActiveMedia expects
        this.setActiveMedia(slideItems[newIndex].dataset.mediaId, false);
      }

      // Event handler using delegation for thumbnail clicks
      handleThumbnailClick(event) {
        // Find the closest button if the event target is inside a thumbnail
        const button = event.target.closest('button');
        if (!button) return;
        
        // Find the parent [data-target] element
        const mediaToSwitch = button.closest('[data-target]');
        if (mediaToSwitch && mediaToSwitch.dataset.target) {
          this.setActiveMedia(mediaToSwitch.dataset.target, false);
          event.preventDefault();
        }
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail && activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        
        if (activeThumbnail) {
          this.setActiveThumbnail(activeThumbnail);
          this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
